/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useParams } from "@packages/framework";
import type { BibleBookId, Notation } from "@packages/sdk";
import {
  BIBLE_HEX,
  URL_BIBLE_IMAGE,
  useBranch,
  useRequestBibleSaveMutations,
  useSession,
} from "@packages/sdk";
import {
  Blockquote,
  composeSlide,
  COPIED_TOAST_TIME,
  CopyIcon,
  cssMediaQueryMinWidthTablet,
  HideFromKids,
  HighlightIcon,
  Journal1Icon,
  Popover,
  PopoverAnchor,
  PopoverContent,
  QuickAction,
  ShareIcon,
  ShareModal,
  spacing,
  Text,
  Toast,
  Transition,
  TransitionRoot,
  useCopyToClipboard,
  useModal,
  useTheme,
  zIndices,
} from "@packages/ui";
import type { MouseEvent, PropsWithChildren } from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { BibleEntry } from "../BibleEntryTracker";
import { bibleProperties } from "./analytics.utilities";
import { BibleNoteModal } from "./BibleNoteModal";
import { Highlights } from "./Highlights";
import { HighlightColors } from "./select.utilities";

const customPush: Record<
  "base" | "entered" | "entering" | "exited" | "exiting",
  Interpolation
> = {
  base: {
    transform: composeSlide("from-bottom", "in"),
    transitionProperty: "transform, opacity",
    width: "min-content",
    willChange: "transform",
  },
  entering: {
    transform: composeSlide("from-bottom", "in"),
  },
  entered: {
    position: "relative",
    transform: composeSlide("from-bottom", "stable"),
  },
  exiting: {
    position: "relative",
    transform: composeSlide("from-top", "stable"),
  },
  exited: {
    transform: composeSlide("from-top", "out"),
  },
};

const actionsStyle: Interpolation = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-evenly",
  alignItems: "center",
  gap: spacing.s,
};

const highlightRoot: Interpolation = {
  left: 0,
  pointerEvents: "none",
  position: "absolute",
  right: 0,
  top: -60,
};
const highlightContainer: Interpolation = {
  pointerEvents: "all",
  width: spacing.full,
  zIndex: -1,
};

type BibleActionsProps = PropsWithChildren<{
  title: string;
  text: string;
  chapter: number;
  start: number;
  end?: number;
  onClose: () => void;
  notations: Notation[];
}>;

export const BibleActions = ({
  title,
  text,
  chapter,
  start,
  end = start,
  onClose,
  notations,
}: BibleActionsProps) => {
  const { translation } = useParams();
  const { translationId, book } = useContext(BibleEntry);
  const { themeContainerRef } = useTheme();
  const shareModal = useModal(ShareModal);
  const branch = useBranch();
  const [, copyToClipboard] = useCopyToClipboard();
  const [showToast, setShowToast] = useState<boolean>(false);
  const newNote = useModal(BibleNoteModal.Create);
  const editNote = useModal(BibleNoteModal.Edit);
  const [highlightColor, setHighlightColor] = useState<HighlightColors | null>(
    null,
  );
  const [showHighlights, setShowHighlights] = useState<boolean>(false);
  const { addNotation, editNotation } = useRequestBibleSaveMutations();
  const [currentNotation, setCurrentNotation] = useState<Notation | null>(null);
  const { status } = useSession();
  const { track } = useAnalytics();

  const saveHighlight = useCallback(
    async (newColor: HighlightColors | null) => {
      if (newColor) {
        track({
          event: HallowAnalyticsEvent.HighlightAdded,
          properties: bibleProperties({
            chapter,
            book_id: book,
            color: newColor,
            type: "notation",
            screen_name: "bible",
            translation_id: translationId,
            translation_name: translation,
            note: currentNotation?.note ?? null,
            verse_start: start,
            verse_end: end,
          }),
        });
        if (currentNotation) {
          const mutation = await editNotation.mutateAsync({
            params: { path: { id: currentNotation.id } },
            body: {
              verse_start: start,
              verse_end: end,
              title,
              note: currentNotation.note?.toString() ?? undefined,
              color: newColor,
              selected_text: text,
            },
          });
          setCurrentNotation(mutation);
        } else {
          const mutation = await addNotation.mutateAsync({
            body: {
              verse_start: start,
              verse_end: end,
              chapter,
              color: newColor,
              title,
              translation_id: translationId,
              book_id: book,
              selected_text: text,
            },
          });
          setCurrentNotation(mutation);
        }
      }
    },
    [
      currentNotation,
      addNotation,
      editNotation,
      text,
      title,
      translationId,
      book,
      start,
      end,
      chapter,
    ],
  );

  const handleNewNote = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      track({
        event: HallowAnalyticsEvent.TappedBibleAction,
        properties: {
          ...bibleProperties({
            type: "notation",
            book_id: book,
            chapter,
            color: highlightColor ?? currentNotation?.color ?? 1,
            verse_start: start,
            verse_end: end,
            note: currentNotation?.note ?? null,
            screen_name: "bible",
            translation_id: translationId,
            translation_name: translation,
          }),
          action: "note",
        },
      });
      if (currentNotation) {
        editNote.show({
          book,
          chapter,
          color: highlightColor ?? currentNotation.color ?? 1,
          end,
          notationId: currentNotation.id,
          note: currentNotation.note?.toString() ?? "",
          start,
          text,
          title,
          translationId,
          translationName: translation,
        });
      } else {
        newNote.show({
          title,
          text,
          chapter,
          start,
          end,
          translationId,
          color: highlightColor ?? 1,
          book,
        });
      }
      onClose();
    },
    [
      highlightColor,
      title,
      text,
      chapter,
      start,
      end,
      translationId,
      book,
      currentNotation,
    ],
  );

  useEffect(() => {
    if (!!text) {
      // something is selected
      if (showHighlights) {
        // the highlight editor is open -- we are adding or editing a highlight
        if (
          !currentNotation ||
          currentNotation.color !== highlightColor ||
          currentNotation.selected_text !== text
        ) {
          saveHighlight(highlightColor);
        }
      } else {
        // we selected something with the highlight closed
        // ergo, we should find the current notation OR that one doesn't exist
        let found: Notation | null = null;
        for (const n of notations) {
          if (
            n.chapter === chapter &&
            n.verse_start === start &&
            n.verse_end === end &&
            n.translation_id === translationId
          ) {
            found = n;
            break;
          }
        }
        setCurrentNotation(found);
        setHighlightColor(found?.color ?? null);
      }
    } else {
      // we deselected everything -- clear the state
      setShowHighlights(false);
      setHighlightColor(null);
      setCurrentNotation(null);
    }
  }, [text, notations, showHighlights, highlightColor]);

  return (
    <Popover key="note" open={!!text} onOpenChange={() => {}}>
      {createPortal(
        <PopoverAnchor css={{ position: "fixed", bottom: 0, left: "50%" }} />,
        themeContainerRef.current,
        "bible-note",
      )}
      {text ? (
        <PopoverContent
          side={"top"}
          css={{
            maxWidth: 300,
            zIndex: zIndices.modalOverModal,
            [cssMediaQueryMinWidthTablet]: { maxWidth: 450 },
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <TransitionRoot
            defaultValue={"none"}
            value={showHighlights ? "highlights" : "none"}
            kind={"custom"}
            baseCSS={customPush.base}
            enteredCSS={customPush.entered}
            enteringCSS={customPush.entering}
            exitedCSS={customPush.exited}
            exitingCSS={customPush.exiting}
            css={highlightRoot}
          >
            <Transition value="highlights" css={highlightContainer}>
              <Highlights
                highlight={highlightColor}
                setHighlight={setHighlightColor}
                variant="popover"
              />
            </Transition>
          </TransitionRoot>
          <Blockquote>
            <Text lineClamp={2} type="bible">
              {text}
            </Text>
            <Text type="title">{title}</Text>
          </Blockquote>
          <div css={actionsStyle}>
            {status === "authenticated" && (
              <>
                <QuickAction
                  icon={<HighlightIcon />}
                  onClick={() => {
                    setShowHighlights((prev) => !prev);
                    if (!showHighlights) {
                      void track({
                        event: HallowAnalyticsEvent.TappedBibleAction,
                        properties: {
                          ...bibleProperties({
                            type: "notation",
                            book_id: book,
                            chapter,
                            color:
                              highlightColor ?? currentNotation?.color ?? 1,
                            verse_start: start,
                            verse_end: end,
                            note: currentNotation?.note ?? null,
                            screen_name: "bible",
                            translation_id: translationId,
                            translation_name: translation,
                          }),
                          action: "highlight",
                        },
                      });
                    }
                    if (!highlightColor)
                      setHighlightColor(HighlightColors.Yellow);
                  }}
                >
                  <>
                    <I18nMessage message="bible_saves_highlight_action" />
                  </>
                </QuickAction>
                <QuickAction icon={<Journal1Icon />} onClick={handleNewNote}>
                  <>
                    <I18nMessage message="bible_note_header" />
                  </>
                </QuickAction>
              </>
            )}
            <QuickAction
              icon={<CopyIcon />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                track({
                  event: HallowAnalyticsEvent.TappedBibleAction,
                  properties: {
                    ...bibleProperties({
                      type: "notation",
                      book_id: book,
                      chapter,
                      color: highlightColor ?? currentNotation?.color ?? 1,
                      verse_start: start,
                      verse_end: end,
                      note: currentNotation?.note ?? null,
                      screen_name: "bible",
                      translation_id: translationId,
                      translation_name: translation,
                    }),
                    action: "copy",
                  },
                });
                track({
                  event: HallowAnalyticsEvent.TappedCopyText,
                  properties: bibleProperties({
                    type: "notation",
                    book_id: book,
                    chapter,
                    color: highlightColor ?? currentNotation?.color ?? 1,
                    verse_start: start,
                    verse_end: end,
                    note: currentNotation?.note ?? null,
                    screen_name: "bible",
                    translation_id: translationId,
                    translation_name: translation,
                  }),
                });
                copyToClipboard(`${text} -${title}, ${translation}`);
                setShowToast(true);
                setTimeout(() => setShowToast(false), COPIED_TOAST_TIME);
              }}
            >
              <>
                <I18nMessage message="web_copy_button_text" />
              </>
            </QuickAction>
            <HideFromKids>
              <QuickAction
                icon={<ShareIcon />}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  track({
                    event: HallowAnalyticsEvent.TappedBibleAction,
                    properties: {
                      ...bibleProperties({
                        type: "notation",
                        book_id: book,
                        chapter,
                        color: highlightColor ?? currentNotation?.color ?? 1,
                        verse_start: start,
                        verse_end: end,
                        note: currentNotation?.note ?? null,
                        screen_name: "bible",
                        translation_id: translationId,
                        translation_name: translation,
                      }),
                      action: "share",
                    },
                  });
                  return shareModal.show({
                    color: BIBLE_HEX,
                    imgSrc: URL_BIBLE_IMAGE,
                    title,
                    link: await branch.bible({
                      translation,
                      book: book as BibleBookId,
                      chapter,
                      verse: start,
                    }),
                  });
                }}
              >
                <>
                  <I18nMessage message="general_word_share" />
                </>
              </QuickAction>
            </HideFromKids>
          </div>
        </PopoverContent>
      ) : null}
      {showToast && (
        <Toast>
          <I18nMessage message="copied_to_clip_confirmation_toast" />
        </Toast>
      )}
    </Popover>
  );
};
